import React from 'react';
import {
  Box,
  LinearProgress,
  linearProgressClasses,
  Typography,
} from '@mui/material';
import Lottie from 'react-lottie';
import { SparkButton, SparkDialog } from 'sharedComponents/UI';
import sparkLoadingAnimation from 'animations/sparkLoading.json';
import { Error, ShareIcon07, Star02, Success } from 'components/SVG';

type Props = {
  closeDialog: () => void;
  open: boolean;
  title: string;
  state: 'loading' | 'error' | 'success' | 'unknown';
  resource?: 'Memory' | 'Spark';
  onRun?: () => void;
  onShare?: (event: React.MouseEvent<HTMLElement>) => void;
  onRetry?: () => void;
};
const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: sparkLoadingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

function PublishResourceModal({
  closeDialog,
  open,
  title,
  state,
  resource = 'Memory',
  onRun = () => {},
  onShare,
}: Props) {
  return (
    <SparkDialog
      onClose={closeDialog}
      open={open}
      title=''
      hideTitle={true}
      hideCloseButton={state === 'loading'}
      disableBackdropClick={state === 'loading'}
    >
      <Box
        display='flex'
        flexDirection='column'
        alignItems='center'
        gap={2}
        paddingY={8}
      >
        {state === 'success' && (
          <>
            <Success width='100px' height='100px' />
            <Typography
              color={(theme) => theme.palette.grey[800]}
              fontFamily='Mont-semibold'
              variant='h4'
              marginTop={2}
            >
              Success
            </Typography>
            <Typography color={(theme) => theme.palette.grey[800]}>
              {title || resource} has been published successfully
            </Typography>
            <Box display='flex' gap={2}>
              <SparkButton
                variant={resource === 'Memory' ? 'contained' : 'outlined'}
                sx={{
                  padding: '10px 25px',
                }}
                startIcon={
                  <ShareIcon07
                    color={
                      resource === 'Memory' ? 'var( --color-primary)' : 'white'
                    }
                    stroke={
                      resource === 'Memory' ? 'white' : 'var( --color-primary)'
                    }
                  />
                }
                onClick={onShare}
              >
                Share {resource}
              </SparkButton>
              {resource === 'Spark' && (
                <SparkButton
                  variant='contained'
                  sx={{
                    padding: '10px 25px',
                  }}
                  startIcon={<Star02 color='white' />}
                  onClick={onRun}
                >
                  Run {resource}
                </SparkButton>
              )}
            </Box>
          </>
        )}
        {state === 'error' && (
          <>
            <Error width='100px' height='100px' />
            <Typography
              color={(theme) => theme.palette.grey[800]}
              fontFamily='Mont-semibold'
              variant='h4'
              marginTop={2}
            >
              Error
            </Typography>
            <Typography color={(theme) => theme.palette.grey[800]}>
              {title || resource} could not be published
            </Typography>
            <Box display='flex' gap={2}>
              <SparkButton
                variant='outlined'
                sx={{
                  padding: '10px 25px',
                }}
                onClick={closeDialog}
              >
                Cancel
              </SparkButton>
              <SparkButton
                variant='contained'
                sx={{
                  padding: '10px 25px',
                }}
              >
                Retry
              </SparkButton>
            </Box>
          </>
        )}
        {state === 'loading' && (
          <>
            <Lottie options={defaultOptions} height={100} width={100} />

            <Typography
              color={(theme) => theme.palette.grey[800]}
              fontFamily='Mont-semibold'
              variant='h4'
            >
              Publishing {resource}
            </Typography>
            <LinearProgress
              variant='indeterminate'
              sx={{
                width: '50%',
                height: '8px',
                borderRadius: '10px',
                [`& .${linearProgressClasses.bar}`]: {
                  borderRadius: 50,
                },
              }}
            />
          </>
        )}
      </Box>
    </SparkDialog>
  );
}

export default PublishResourceModal;
